/**
 * archie_result_fetcher.js
 *
 * Port of neighborhood/server/server.js  FetchResult_fr_SceneDB + process*response helpers.
 *
 * After the arche-engine job completes, the raw `result` object must be enriched with
 * actual scene data fetched from the TwinUp SceneDB.  This module handles that step so
 * Scenes.js stays lean.
 *
 * Public API
 * ----------
 *   fetchAndFormatArchieResult(resultValue, accessToken, getBaseUrl, formatBearerToken)
 *     → Promise<{ response, tags, [entities], [csv_representation] }>
 */

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Fetch one stage from the TwinUp SceneDB.
 * @param {string|number} sceneId
 * @param {string|number} stageId
 * @param {number} layerDepth  1 = no sub_layers, 2 = with sub_layers
 * @param {string} accessToken
 * @param {Function} getBaseUrl
 * @param {Function} formatBearerToken
 */
const fetchStage = async (sceneId, stageId, layerDepth, accessToken, getBaseUrl, formatBearerToken) => {
    const url = `${getBaseUrl()}/api/v2/scene/${sceneId}/stage/${stageId}?layer_depth=${layerDepth}`;
    const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': formatBearerToken(accessToken)
        }
    });
    if (!res.ok) {
        throw new Error(`Failed to fetch stage ${stageId}: HTTP ${res.status}`);
    }
    return res.json();
};

const fetchLayer = async (sceneId, stageId, layerId, layerDepth, accessToken, getBaseUrl, formatBearerToken) => {
    const url = `${getBaseUrl()}/api/v2/scene/${sceneId}/stage/${stageId}/layer/${layerId}?layer_depth=${layerDepth}`;
    const res = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': formatBearerToken(accessToken)
        }
    });
    if (!res.ok) {
        throw new Error(`Failed to fetch Layer ${layerId}: HTTP ${res.status}`);
    }
    return res.json();
};

// ---------------------------------------------------------------------------
// Entity extraction helper
// ---------------------------------------------------------------------------

/**
 * Scan an array of prims for `entity_ids` / `element_ids` attributes.
 * Returns:
 *   entityIds      – deduplicated flat list of ID strings
 *   entityTooltipMap – { [id]: { _label, ...otherAttributes } }
 *                     used as per-entity tooltip data when highlighting on the map.
 *
 * @param {Array}  prims        Array of prim objects (each has .attributes[])
 * @param {string} tooltipLabel Human-readable context label (e.g. sublayer name)
 */
const extractEntityData = (prims, tooltipLabel = '') => {
    const entityIds = [];
    const entityTooltipMap = {};
    for (const prim of (prims || [])) {
        const attrMap = {};
        for (const attr of (prim.attributes || [])) attrMap[attr.name] = attr.value;

        // ── Path 1: explicit entity_ids / element_ids attribute ──────────────
        const rawIds = attrMap['entity_ids'] || attrMap['element_ids'];
        if (rawIds) {
            const ids = String(rawIds).split(',').map(s => s.trim()).filter(Boolean);
            const tooltip = { _label: tooltipLabel || prim.name, ...attrMap };
            delete tooltip['entity_ids'];
            delete tooltip['element_ids'];
            for (const id of ids) {
                if (!entityIds.includes(id)) entityIds.push(id);
                // Keep first occurrence; later prims may have different data
                if (!entityTooltipMap[id]) entityTooltipMap[id] = tooltip;
            }
            continue;
        }

        // ── Path 2: prim.name is itself a numeric entity ID ──────────────────
        // Some KPI responses store per-element EffectPrims named with the
        // element ID (e.g. name="14330") rather than using an entity_ids attr.
        if (prim.type === 'EffectPrim' && prim.name && /^\d+$/.test(String(prim.name).trim())) {
            const id = String(prim.name).trim();
            if (!entityIds.includes(id)) entityIds.push(id);
            if (!entityTooltipMap[id]) {
                entityTooltipMap[id] = { _label: tooltipLabel || id, ...attrMap };
            }
        }
    }
    return { entityIds, entityTooltipMap };
};

/**
 * Build an entity tooltip map by assigning a sublayer's EffectPrim attribute data
 * to every entity_id found at the layer (ContextPrim) level.
 *
 * One sublayer per layer (one KPI):
 *   - entity_ids live on layer ContextPrims  → which elements to highlight
 *   - EffectPrim attrs on the single sublayer → KPI data to show on hover
 *   - Different layers have different entity_ids → no collision across layers
 *
 * @param {string[]} layerEntityIds  IDs from the layer's ContextPrim
 * @param {Array}    sublayerPrims   Prims from the sub_layer
 * @param {string}   label           Tooltip header label (sublayer name)
 * @returns {{ [id]: tooltipData }}
 */
const buildSublayerTooltipMap = (layerEntityIds, sublayerPrims, label = '') => {
    if (!layerEntityIds?.length) return {};
    const sublayerAttrs = { _label: label };
    for (const prim of (sublayerPrims || [])) {
        if (prim.type !== 'EffectPrim' && prim.type !== 'ContextPrim') continue;
        for (const attr of (prim.attributes || [])) {
            if (attr.name !== 'entity_ids' && attr.name !== 'element_ids') {
                sublayerAttrs[attr.name] = attr.value;
            }
        }
    }
    if (Object.keys(sublayerAttrs).length <= 1) return {};   // only _label, no real data
    const result = {};
    for (const id of layerEntityIds) result[id] = sublayerAttrs;
    return result;
};

/** Structural response — layer_depth=1, reads ContextPrim attributes */
const processStructuralResponse = async (stage_ids, layer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const allEntityIds = [];
    const allEntityTooltipMap = {};
    const jsonresponse = {};
    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        const stageData = await fetchStage(scene_id, stage_id, 0, accessToken, getBaseUrl, formatBearerToken);
        jsonresponse[stageData.name] = {};
        for (const layer_id of layer_ids) {
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 0, accessToken, getBaseUrl, formatBearerToken);
            jsonresponse[stageData.name][layer.name] = {};
            for (const prim of layer.prims) {
                if (prim.type === 'ContextPrim') {
                    for (const attribute of prim.attributes) {
                        jsonresponse[stageData.name][layer.name][attribute.name] = attribute.value;
                    }
                }
            }
            // Extract entity_ids from all prims in this layer
            const { entityIds, entityTooltipMap } = extractEntityData(layer.prims, layer.name);
            entityIds.forEach(id => { if (!allEntityIds.includes(id)) allEntityIds.push(id); });
            Object.assign(allEntityTooltipMap, entityTooltipMap);
        }
    }
    return { response: jsonresponse, entities: allEntityIds, entityIds: allEntityIds, entityTooltipMap: allEntityTooltipMap, tags };
};

/**
 * Asset Query response — fetches layer/prim data to extract entity_ids for highlighting.
 * 
 * Similar to energy/carbon responses but DOES NOT render any UI in the left panel.
 * Only returns entityIds + entityTooltipMap for map highlighting.
 * 
 * Used for queries like "show me walls", "highlight all doors", etc.
 * 
 * @param {Array}    stage_ids
 * @param {Object}   layer_ids_map     { stage_id: [layer_ids] }
 * @param {string}   scene_id
 * @param {string}   accessToken
 * @param {Object}   tags              Response tags from arche-engine
 * @param {Function} getBaseUrl
 * @param {Function} formatBearerToken
 * @returns {Promise<{ response: null, entityIds: string[], entityTooltipMap: object, tags: object }>}
 */
const processAssetQueryResponse = async (stage_ids, layer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const allEntityIds = [];
    const allEntityTooltipMap = {};
    
    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        if (!layer_ids || layer_ids.length === 0) continue;
        
        for (const layer_id of layer_ids) {
            // Fetch layer WITH prim attributes to get entity_ids
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 0, accessToken, getBaseUrl, formatBearerToken);
            
            // Extract component label from layer name (format: "ComponentType-floorname")
            const dashIdx = layer.name.indexOf('-');
            const componentLabel = dashIdx > -1 ? layer.name.slice(0, dashIdx) : layer.name;
            
            // Process each prim individually - each prim has ONE entity_id and its own data
            for (const prim of (layer.prims || [])) {
                // Build attribute map for this specific prim
                const attrMap = {};
                for (const attr of (prim.attributes || [])) {
                    attrMap[attr.name] = attr.value;
                }
                
                // Get entity_id from this prim's attributes
                const entityIdValue = attrMap['entity_ids'] || attrMap['element_ids'];
                if (!entityIdValue) continue;
                
                // Parse entity_id (could be string or number)
                const entityId = String(entityIdValue).trim();
                if (!entityId) continue;
                
                // Add to global list
                if (!allEntityIds.includes(entityId)) {
                    allEntityIds.push(entityId);
                }
                
                // Build tooltip data from THIS prim's attributes (excluding entity_ids itself)
                const tooltipData = { _label: componentLabel };
                for (const [key, value] of Object.entries(attrMap)) {
                    if (key !== 'entity_ids' && key !== 'element_ids') {
                        tooltipData[key] = value;
                    }
                }
                
                // Map THIS entity_id to THIS prim's data
                allEntityTooltipMap[entityId] = tooltipData;
            }
        }
    }
    
    console.log(`🎯 [asset-query] Extracted ${allEntityIds.length} entities for highlighting`);
    
    // Return NULL response so nothing renders in the left panel
    // Only entityIds + entityTooltipMap are used for map highlighting
    return { 
        response: null, 
        entityIds: allEntityIds, 
        entityTooltipMap: allEntityTooltipMap, 
        tags 
    };
};

/** Comfort response — reads all attributes from the Comfort_metrics EffectPrim */
const processComfortResponse = async (stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const jsonresponse = {};
    const allEntityIds = [];
    const allEntityTooltipMap = {};
    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        if (!layer_ids || layer_ids.length === 0) continue;
        for (const layer_id of layer_ids) {
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 1, accessToken, getBaseUrl, formatBearerToken);

            // Only process layers that have an entry in sublayer_ids_map
            if (!sublayer_ids_map[layer.id.toString()]) continue;

            const sublayer_ids = (sublayer_ids_map[layer.id.toString()] || []).map(String);

            // layer.name format: "ComponentType-floorname" e.g. "Thermal_Comfort-firstfloor"
            const dashIdx       = layer.name.indexOf('-');
            const componentLabel = dashIdx > -1 ? layer.name.slice(0, dashIdx) : layer.name;
            const floorKey       = dashIdx > -1 ? layer.name.slice(dashIdx + 1) : layer.name;

            if (!jsonresponse[floorKey]) jsonresponse[floorKey] = {};

            // Step 1: entity_ids from layer prims
            const layerExtraction = extractEntityData(layer.prims || [], componentLabel);
            layerExtraction.entityIds.forEach(id => { if (!allEntityIds.includes(id)) allEntityIds.push(id); });

            // Step 2: KPI data from this layer's sublayer(s)
            const sublayerAttrs = {};
            for (const sublayer of (layer.sub_layers || [])) {
                if (!sublayer_ids.includes(String(sublayer.id))) continue;
                for (const prim of (sublayer.prims || [])) {
                    if (prim.type === 'EffectPrim' && prim.name === 'Comfort_metrics') {
                        for (const attr of prim.attributes) {
                            jsonresponse[floorKey][attr.name] = attr.value;
                            if (attr.name !== 'entity_ids' && attr.name !== 'element_ids') {
                                sublayerAttrs[attr.name] = attr.value;
                            }
                        }
                    }
                }
            }

            // Step 3: map ALL of this layer's entity_ids → this layer's comfort data
            if (layerExtraction.entityIds.length > 0 && Object.keys(sublayerAttrs).length > 0) {
                const tooltipPayload = { _label: componentLabel, _floor: floorKey, ...sublayerAttrs };
                for (const id of layerExtraction.entityIds) {
                    allEntityTooltipMap[id] = tooltipPayload;
                }
            }
        }
    }
    return { response: jsonresponse, entityIds: allEntityIds, entityTooltipMap: allEntityTooltipMap, tags };
};


const processCostResponse = async (stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const cost_data = {};
    const allEntityIds = [];
    const allEntityTooltipMap = {};
    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        if (!layer_ids || layer_ids.length === 0) continue;
        const stageData = await fetchStage(scene_id, stage_id, 0, accessToken, getBaseUrl, formatBearerToken);
        cost_data[stageData.name] = {};
        for (const layer_id of layer_ids) {
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 1, accessToken, getBaseUrl, formatBearerToken);
            cost_data[stageData.name][layer.name] = {};
            const sublayer_ids = (sublayer_ids_map[layer.id.toString()] || []).map(String);

            // layer.name format: "ComponentType-floorname" e.g. "Wall-firstfloor"
            const dashIdx        = layer.name.indexOf('-');
            const componentLabel = dashIdx > -1 ? layer.name.slice(0, dashIdx) : layer.name;

            // Step 1: entity_ids from layer prims
            const layerExtraction = extractEntityData(layer.prims || [], componentLabel);
            layerExtraction.entityIds.forEach(id => { if (!allEntityIds.includes(id)) allEntityIds.push(id); });

            // Step 2: process sublayer prims
            // Structure of a cost sublayer:
            //   - Material_Cost_Prim (EffectPrim)  → sum of all material ContextPrims — tooltip only, SKIP from CSV
            //   - ContextPrims (Gypsum Board, Metal Furring …) → individual material line items → CSV rows
            //   - Labor_Cost_Prim (EffectPrim)      → labor total → CSV row + tooltip
            //   - Net_Cost_Prim (EffectPrim)        → grand total → tooltip only, SKIP from CSV
            const costTooltip = { _label: componentLabel };

            for (const sublayer of (layer.sub_layers || [])) {
                if (!sublayer_ids.includes(String(sublayer.id))) continue;
                for (const prim of (sublayer.prims || [])) {
                    if (prim.type === 'MethodPrim') continue;

                    const attrMap = {};
                    for (const attr of prim.attributes) attrMap[attr.name] = attr.value;

                    if (prim.name === 'Net_Cost_Prim') {
                        // Grand total — tooltip only
                        if (attrMap['Net Cost'] !== undefined) costTooltip['Total Cost'] = attrMap['Net Cost'];
                        if (!costTooltip['Currency']) costTooltip['Currency'] = attrMap['Currency'] || '';
                        continue;
                    }

                    if (prim.name === 'Material_Cost_Prim') {
                        // Material summary — tooltip only.
                        // Its Net Cost = sum of all material ContextPrims, so adding it to
                        // cost_data would double-count every material item in the CSV.
                        if (attrMap['Net Cost'] !== undefined) costTooltip['Material Cost'] = attrMap['Net Cost'];
                        if (!costTooltip['Currency']) costTooltip['Currency'] = attrMap['Currency'] || '';
                        continue;
                    }

                    if (prim.type !== 'EffectPrim' && prim.type !== 'ContextPrim') continue;

                    // Labor EffectPrim (and any other non-material EffectPrims) — CSV + tooltip
                    if (prim.type === 'EffectPrim') {
                        if (attrMap['Net Cost'] !== undefined) {
                            const label = attrMap['name'] || prim.name.replace(/_Cost_Prim$/, '');
                            costTooltip[`${label} Cost`] = attrMap['Net Cost'];
                            if (!costTooltip['Currency']) costTooltip['Currency'] = attrMap['Currency'] || '';
                        }
                    }

                    // Add to cost_data for CSV (ContextPrims + Labor EffectPrim)
                    cost_data[stageData.name][layer.name][prim.name] = {};
                    for (const attr of prim.attributes) {
                        cost_data[stageData.name][layer.name][prim.name][attr.name] = attr.value;
                    }
                }
            }

            // Step 3: map ALL of this layer's entity_ids → structured cost tooltip
            if (layerExtraction.entityIds.length > 0) {
                for (const id of layerExtraction.entityIds) {
                    allEntityTooltipMap[id] = costTooltip;
                }
            }
        }
    }

    const csvRows = ['Cost Category,Cost Sub-category,Cost Element,Rate (per unit),Quantity,Unit Type,Net Cost,Currency'];
    for (const category of Object.keys(cost_data)) {
        for (const element of Object.keys(cost_data[category])) {
            for (const detailKey of Object.keys(cost_data[category][element])) {
                const entry = cost_data[category][element][detailKey];
                if (entry) {
                    csvRows.push([
                        category, element,
                        entry['name'], entry['Rate(per unit)'],
                        entry['Quantity'], entry['Unit type'],
                        entry['Net Cost'], entry['Currency']
                    ].join(','));
                }
            }
        }
    }
    return { response: csvRows.join('\n'), csv_representation: true, entityIds: allEntityIds, entityTooltipMap: allEntityTooltipMap, tags };
};

/**
 * Energy Demand response — reads only the Floor EffectPrim from each sublayer.
 *
 * Multiple layers (e.g. FlexDuct, Pipe, Cable…) on the same floor all carry an
 * identical Floor EffectPrim with the floor-level totals. We therefore:
 *   1. Deduplicate by floor name so each floor appears only once.
 *   2. Expand the four demand types (heating / cooling / lighting / equipment)
 *      into separate subsystem keys so EnergyWidget renders them as distinct
 *      pie slices rather than a single undifferentiated floor total.
 *
 * Result shape passed to EnergyWidget:
 *   {
 *     "firstfloor": {
 *       "heating_demand":   { total_demand: 18333.52,  unit: "kWh" },
 *       "cooling_demand":   { total_demand: 258327.66, unit: "kWh" },
 *       "lighting_demand":  { total_demand: 274897.33, unit: "kWh" },
 *       "equipment_demand": { total_demand: 189425.05, unit: "kWh" }
 *     }
 *   }
 */
const ENERGY_DEMAND_KEYS = ['heating_demand', 'cooling_demand', 'lighting_demand', 'equipment_demand'];

const processEnergyDemandResponse = async (stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const floorDataMap    = {};   // floorName -> { unit, heating_demand, ... }
    const componentsByFloor = {}; // floorName -> { componentLabel -> { demand values } }
    const allEntityIds    = [];
    const allEntityTooltipMap = {};

    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        if (!layer_ids || layer_ids.length === 0) continue;

        for (const layer_id of layer_ids) {
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 1, accessToken, getBaseUrl, formatBearerToken);
            const sublayer_ids = (sublayer_ids_map[layer.id.toString()] || []).map(String);

            // ── Step 1: parse layer name → component label + floor ────────────
            // Convention: "ComponentType-floorname" e.g. "AirTerminal-firstfloor"
            const dashIdx      = layer.name.indexOf('-');
            const componentLabel = dashIdx > -1 ? layer.name.slice(0, dashIdx) : layer.name;
            const floorFromName  = dashIdx > -1 ? layer.name.slice(dashIdx + 1) : layer.name;

            // ── Step 2: get this layer's entity_ids from layer-level prims ────
            const layerExtraction = extractEntityData(layer.prims || [], componentLabel);
            layerExtraction.entityIds.forEach(id => { if (!allEntityIds.includes(id)) allEntityIds.push(id); });

            // ── Step 3: read demand data from this layer's sublayer(s) ────────
            let layerUnit     = 'kWh';
            let layerFloorKey = floorFromName;
            const layerDemandAcc = {
                ...ENERGY_DEMAND_KEYS.reduce((a, k) => ({ ...a, [k]: 0 }), {}),
                total_demand: 0
            };

            for (const sublayer of (layer.sub_layers || [])) {
                if (!sublayer_ids.includes(String(sublayer.id))) continue;

                for (const prim of (sublayer.prims || [])) {
                    if (prim.type !== 'EffectPrim') continue;
                    const attrMap = {};
                    for (const attr of prim.attributes) attrMap[attr.name] = attr.value;

                    if (prim.name === 'Floor') {
                        const floorName = attrMap.name || floorFromName;
                        layerFloorKey = floorName;
                        if (!floorDataMap[floorName]) {
                            floorDataMap[floorName] = {
                                unit: attrMap.unit || 'kWh',
                                ...ENERGY_DEMAND_KEYS.reduce((acc, k) => {
                                    if (attrMap[k] !== undefined) acc[k] = parseFloat(attrMap[k]);
                                    return acc;
                                }, {})
                            };
                        }
                        if (!componentsByFloor[floorName]) componentsByFloor[floorName] = {};
                    }

                    if (prim.name === 'Entity') {
                        layerUnit = attrMap.unit || 'kWh';
                        ENERGY_DEMAND_KEYS.forEach(k => {
                            if (attrMap[k] !== undefined) layerDemandAcc[k] += parseFloat(attrMap[k]) || 0;
                        });
                        if (attrMap.total_demand !== undefined) {
                            layerDemandAcc.total_demand += parseFloat(attrMap.total_demand) || 0;
                        }
                        if (!componentsByFloor[layerFloorKey]) componentsByFloor[layerFloorKey] = {};
                        if (!componentsByFloor[layerFloorKey][componentLabel]) {
                            componentsByFloor[layerFloorKey][componentLabel] = {
                                ...ENERGY_DEMAND_KEYS.reduce((acc, k) => ({
                                    ...acc, [k]: attrMap[k] !== undefined ? parseFloat(attrMap[k]) : 0
                                }), {}),
                                total_demand: attrMap.total_demand !== undefined ? parseFloat(attrMap.total_demand) : 0,
                                unit: attrMap.unit || 'kWh',
                            };
                        }
                    }
                }
            }

            // ── Step 4: map ALL of this layer's entity_ids → this layer's data ─
            if (layerExtraction.entityIds.length > 0) {
                const floorTotals      = floorDataMap[layerFloorKey] || {};
                const floorTotalDemand = ENERGY_DEMAND_KEYS.reduce((sum, k) => sum + (floorTotals[k] || 0), 0);

                // ── Entity / component-level demand (from Entity EffectPrim) ───
                // These are the demands for this component type on this floor.
                // If the Entity EffectPrim had no individual breakdown, the values
                // will be 0 and we leave them out (don't fall back to floor values —
                // that would mislead the user into thinking a single duct = floor total).
                const entityDemandFields = ENERGY_DEMAND_KEYS.reduce((acc, k) => {
                    if (layerDemandAcc[k] > 0) acc[k] = layerDemandAcc[k];
                    return acc;
                }, {});

                // ── Floor-level breakdown (from Floor EffectPrim) ─────────────
                // Stored with a "floor_" prefix so formatTooltipHtml can render
                // them as a separate section below the entity values.
                const floorBreakdownFields = ENERGY_DEMAND_KEYS.reduce((acc, k) => {
                    if ((floorTotals[k] || 0) > 0) acc[`floor_${k}`] = floorTotals[k];
                    return acc;
                }, {});

                const tooltipPayload = {
                    _label:            componentLabel,
                    _floor:            layerFloorKey,
                    _floor_total:      floorTotalDemand > 0 ? floorTotalDemand : undefined,
                    _floor_unit:       floorTotals.unit || 'kWh',
                    unit:              layerUnit,
                    // Entity-level section (shown first)
                    ...entityDemandFields,
                    ...(layerDemandAcc.total_demand > 0 ? { total_demand: layerDemandAcc.total_demand } : {}),
                    // Floor breakdown section (shown after a separator)
                    ...floorBreakdownFields,
                };

                let overwroteCount = 0;
                for (const id of layerExtraction.entityIds) {
                    if (allEntityTooltipMap[id] !== undefined) overwroteCount++;
                    allEntityTooltipMap[id] = tooltipPayload;
                }
                console.log(`🔌 [energy] "${componentLabel}" floor="${layerFloorKey}" → ${layerExtraction.entityIds.length} IDs, total_demand=${layerDemandAcc.total_demand.toFixed(2)}, overwrote=${overwroteCount}`);
            }
        }
    }

    // Transform into EnergyWidget-compatible structure
    // ── Diagnostic: show label distribution in the final tooltip map ──────────
    const labelDist = {};
    for (const v of Object.values(allEntityTooltipMap)) {
        const l = v._label || '?';
        labelDist[l] = (labelDist[l] || 0) + 1;
    }
    console.log(`🏢 [energy] tooltip map TOTAL entries: ${Object.keys(allEntityTooltipMap).length}`);
    console.log('🏢 [energy] label distribution (should show all component types):', JSON.stringify(labelDist));
    const tooltipSample = Object.entries(allEntityTooltipMap).slice(0, 3);
    console.log('🏢 [energy] first 3 entries:', tooltipSample.map(([id, d]) => `${id} → _label="${d._label}" floor="${d._floor}" total=${d.total_demand}`));

    const jsonresponse = {};
    for (const [floorName, floorData] of Object.entries(floorDataMap)) {
        jsonresponse[floorName] = {};
        const unit = floorData.unit || 'kWh';
        for (const demandKey of ENERGY_DEMAND_KEYS) {
            if (floorData[demandKey] !== undefined && floorData[demandKey] > 0) {
                jsonresponse[floorName][demandKey] = {
                    total_demand: floorData[demandKey],
                    unit
                };
            }
        }
    }

    return { response: jsonresponse, energyComponents: componentsByFloor, entityIds: allEntityIds, entityTooltipMap: allEntityTooltipMap, tags };
};

/**
 * Solar Radiation response — separates overview EffectPrim from N/S/E/W directional EffectPrims.
 *
 * Result shape:
 *   { "<stageName>": {
 *       "<layerName>": {
 *         overview:    { radiation_rate_avg, sunlight_hours_per_day, shadow_duration_percent,
 *                        sky_view_factor, solar_access_index, direct_irradiance, diffuse_irradiance,
 *                        reflected_irradiance, solar_heat_gain, annotation },
 *         directions:  { North: { intensity, rate, peak_sun_hours },
 *                        South: { ... }, East: { ... }, West: { ... } }
 *       }
 *   }}
 */
const SOLAR_OVERVIEW_KEYS = [
    'radiation_rate_avg', 'sunlight_hours_per_day', 'shadow_duration_percent',
    'sky_view_factor', 'solar_access_index', 'direct_irradiance', 'diffuse_irradiance',
    'reflected_irradiance', 'solar_heat_gain', 'annual_radiation_total', 'annotation'
];

const processSolarRadiationResponse = async (stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const jsonresponse = {};
    const allEntityIds = [];
    const allEntityTooltipMap = {};

    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        if (!layer_ids || layer_ids.length === 0) continue;
        const stageData = await fetchStage(scene_id, stage_id, 0, accessToken, getBaseUrl, formatBearerToken);
        jsonresponse[stageData.name] = {};

        for (const layer_id of layer_ids) {
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 1, accessToken, getBaseUrl, formatBearerToken);
            const sublayer_ids = (sublayer_ids_map[layer.id.toString()] || []).map(String);
            const layerEntry = { overview: {}, directions: {} };

            // layer.name format: "ComponentType-floorname" e.g. "Facade-firstfloor"
            const dashIdx        = layer.name.indexOf('-');
            const componentLabel = dashIdx > -1 ? layer.name.slice(0, dashIdx) : layer.name;

            // Step 1: entity_ids from layer prims
            const layerExtraction = extractEntityData(layer.prims || [], componentLabel);
            layerExtraction.entityIds.forEach(id => { if (!allEntityIds.includes(id)) allEntityIds.push(id); });

            // Step 2: read sublayer data (overview + directional)
            for (const sublayer of (layer.sub_layers || [])) {
                if (!sublayer_ids.includes(String(sublayer.id))) continue;
                for (const prim of (sublayer.prims || [])) {
                    if (prim.type !== 'EffectPrim') continue;
                    const attrMap = {};
                    for (const attr of prim.attributes) attrMap[attr.name] = attr.value;
                    const dir = attrMap['cardinal_direction'];
                    if (dir) {
                        const prefix = dir.toLowerCase() + '_';
                        layerEntry.directions[dir] = {
                            intensity:      parseFloat(attrMap[`${prefix}annual_radiation_intensity`]) || 0,
                            rate:           parseFloat(attrMap[`${prefix}radiation_rate_avg`])         || 0,
                            peak_sun_hours: parseFloat(attrMap[`${prefix}peak_sun_hours`])             || 0
                        };
                    } else {
                        for (const key of SOLAR_OVERVIEW_KEYS) {
                            if (attrMap[key] !== undefined) layerEntry.overview[key] = attrMap[key];
                        }
                    }
                }
            }

            // Step 3: map ALL of this layer's entity_ids → this layer's overview data
            if (layerExtraction.entityIds.length > 0 && Object.keys(layerEntry.overview).length > 0) {
                const tooltipPayload = { _label: componentLabel, ...layerEntry.overview };
                for (const id of layerExtraction.entityIds) {
                    allEntityTooltipMap[id] = tooltipPayload;
                }
            }

            jsonresponse[stageData.name][layer.name] = layerEntry;
        }
    }

    return { response: jsonresponse, entityIds: allEntityIds, entityTooltipMap: allEntityTooltipMap, tags };
};

/** Generic/other response — layer_depth=1, flattens all EffectPrim attributes */
const processOtherResponse = async (stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, tags, getBaseUrl, formatBearerToken) => {
    const jsonresponse = {};
    const allEntityIds = [];
    const allEntityTooltipMap = {};
    for (const stage_id of stage_ids) {
        const layer_ids = layer_ids_map[stage_id.toString()];
        const stageData = await fetchStage(scene_id, stage_id, 0, accessToken, getBaseUrl, formatBearerToken);
        jsonresponse[stageData.name] = {};
        for (const layer_id of layer_ids) {
            const layer = await fetchLayer(scene_id, stage_id, layer_id, 1, accessToken, getBaseUrl, formatBearerToken);
            jsonresponse[stageData.name][layer.name] = {};
            const sublayer_ids = (sublayer_ids_map[layer.id.toString()] || []).map(String);

            // layer.name format: "ComponentType-floorname" e.g. "Wall-firstfloor"
            const dashIdx        = layer.name.indexOf('-');
            const componentLabel = dashIdx > -1 ? layer.name.slice(0, dashIdx) : layer.name;

            // Step 1: entity_ids from layer prims
            const layerExtraction = extractEntityData(layer.prims || [], componentLabel);
            layerExtraction.entityIds.forEach(id => { if (!allEntityIds.includes(id)) allEntityIds.push(id); });

            // Step 2: collect KPI data from this layer's sublayer(s)
            const sublayerAttrs = {};
            for (const sublayer of (layer.sub_layers || [])) {
                if (!sublayer_ids.includes(String(sublayer.id))) continue;
                for (const prim of (sublayer.prims || [])) {
                    if (prim.type === 'EffectPrim') {
                        for (const attr of prim.attributes) {
                            jsonresponse[stageData.name][layer.name][attr.name] = attr.value;
                            if (attr.name !== 'entity_ids' && attr.name !== 'element_ids') {
                                sublayerAttrs[attr.name] = attr.value;
                            }
                        }
                    }
                }
            }

            // Step 3: map ALL of this layer's entity_ids → this layer's KPI data
            if (layerExtraction.entityIds.length > 0 && Object.keys(sublayerAttrs).length > 0) {
                const tooltipPayload = { _label: componentLabel, ...sublayerAttrs };
                for (const id of layerExtraction.entityIds) {
                    allEntityTooltipMap[id] = tooltipPayload;
                }
            }
        }
    }
    return { response: jsonresponse, entityIds: allEntityIds, entityTooltipMap: allEntityTooltipMap, tags };
};

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Port of server.js FetchResult_fr_SceneDB.
 *
 * Takes the raw `result` value from arche-engine GET /status/{id}, fetches the
 * relevant stage / layer / prim data from TwinUp SceneDB, and returns a
 * formatted object ready for renderArchieResponse.
 *
 * `getBaseUrl` and `formatBearerToken` are passed in so this module stays
 * free of React / component-level imports.
 *
 * @param {object|string} resultValue       Full result from arche-engine /status
 * @param {string}        accessToken
 * @param {Function}      getBaseUrl        () => string
 * @param {Function}      formatBearerToken (token: string) => string
 * @returns {Promise<{ response, tags, entities?, csv_representation? }>}
 */
export const fetchAndFormatArchieResult = async (resultValue, accessToken, getBaseUrl, formatBearerToken) => {
    // Normalise to object
    if (typeof resultValue === 'string') {
        resultValue = JSON.parse(resultValue);
    }

    // Advice-mode responses — `advice` lives at the top level of the parsed object,
    // NOT inside `tags`. Detect by either the advice key being present or result_type === 'advice'.
    if (resultValue?.advice || resultValue?.tags?.result_type === 'advice') {
        return {
            advice:   resultValue.advice || '',
            response: resultValue.advice || '',
            tags:     resultValue.tags || { data_characteristics: ['text'], result_type: 'advice' }
        };
    }

    // Extract inner result object
    let result = resultValue.result;
    if (typeof result === 'string') {
        result = JSON.parse(result);
    }
    if (!result) {
        throw new Error('No result object found in arche-engine response');
    }

    const { scene_id, stage_ids, layer_ids_map, sublayer_ids_map } = result;

    if (!scene_id || !Array.isArray(stage_ids) || !layer_ids_map) {
        throw new Error('Invalid result structure from arche-engine');
    }

    const dataChars = resultValue?.tags?.data_characteristics || [];

    // Compliance: pass the full raw resultValue through untouched so CompliancePanel
    // can access result.sublayer_ids_map, compliance_subtask, building_id, etc.
    if (dataChars.includes('compliance')) {
        return {
            ...resultValue,           // preserves result, compliance_subtask, building_id, etc.
            response: null,
            tags: resultValue.tags,
        };
    }

    // Asset Query: "show me walls", "highlight doors", etc.
    // These should highlight entities but NOT show any widget in the left panel
    if (dataChars.includes('Fetch') || dataChars.includes('Asset Query')) {
        return processAssetQueryResponse(stage_ids, layer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
    }

    // Structural: layer_depth=1 only
    if (dataChars.includes('Fetch')) {
        return processStructuralResponse(stage_ids, layer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
    }

    // All other types need sublayer_ids_map
    if (!sublayer_ids_map) {
        throw new Error('Invalid result structure: sublayer_ids_map required');
    }

    if (dataChars.includes('cost')) {
        return processCostResponse(stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
    }
    if (dataChars.includes('Comfort')) {
        return processComfortResponse(stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
    }
    if (dataChars.includes('Energy Demand')) {
        return processEnergyDemandResponse(stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
    }
    if (dataChars.includes('Solar Radiation') || dataChars.includes('shadow')) {
        return processSolarRadiationResponse(stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
    }
    return processOtherResponse(stage_ids, layer_ids_map, sublayer_ids_map, scene_id, accessToken, resultValue.tags, getBaseUrl, formatBearerToken);
};
